import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, 
  UserCircle, 
  Briefcase, 
  AlertCircle, 
  Handshake,
  Users,
  TrendingUp,
  Shield,
  ChevronDown,
  X,
  ArrowLeft
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { dashboardAPI } from '../api/dashboard.api';
import { jointVenturesAPI } from '../api/jointVentures.api';
import { counties } from '../lib/locations';

const TITLE_DEED_STATUS = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'pending', label: 'Pending Transfer' }
];

const DEVELOPMENT_TYPES = [
  'Residential Apartments',
  'Commercial Building',
  'Mixed-Use Development',
  'Rental Units',
  'Townhouses',
  'Warehouses',
  'Hotel/Resort',
  'Shopping Center',
  'Other'
];

const TIMELINES = [
  '3-6 months',
  '6-12 months',
  '1-2 years',
  '2-3 years',
  '3+ years'
];

const EXPERIENCE_LEVELS = [
  { value: 'first_time', label: 'First Time Investor' },
  { value: 'experienced', label: 'Experienced (1-3 projects)' },
  { value: 'professional', label: 'Professional (4+ projects)' }
];

// ============================================
// LANDING PAGE COMPONENT
// ============================================
function JVLandingPage({ onNavigateToForm, onBack }) {
  const [showLandowners, setShowLandowners] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <div className="container py-6 sm:py-8 md:py-12 max-w-5xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
        Back to Dashboard
      </Button>

      {/* Hero Section */}
      <div className="text-center mb-6 sm:mb-8 md:mb-12">
        <div className="flex justify-center mb-3 sm:mb-4">
          <Handshake className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-primary" />
        </div>
        <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">
          Joint Venture Platform
        </h1>
        <p className="text-xs sm:text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
          Connecting land owners with investors for real estate development projects
        </p>
      </div>

      {/* Landowner Match Items - Collapsible */}
      <Card className="mb-3 sm:mb-4 md:mb-6">
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors relative"
          onClick={() => setShowLandowners(!showLandowners)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base md:text-lg">
              What We Do
            </CardTitle>
            {showLandowners ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>

        {showLandowners && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">For Land Owners</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Connect with verified investors looking to develop land projects. Get matched based on location, project type, and capital requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">For Investors</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Find prime land opportunities for development. Access pre-vetted land owners and negotiate mutually beneficial partnerships.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">Private & Secure</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Your information remains confidential. We only connect you with serious, verified parties based on your criteria.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <Handshake className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">Smart Matching</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Our algorithm matches land owners and investors based on project compatibility, location, and investment requirements.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* How It Works - Collapsible */}
      <Card className="mb-6 sm:mb-8">
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors relative"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base md:text-lg">How It Works</CardTitle>
            {showHowItWorks ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>

        {showHowItWorks && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-medium mb-0.5">Submit Your Interest</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Fill out the form with your land details or investment criteria
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-medium mb-0.5">Get Matched</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Our system finds compatible partners based on your requirements
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-medium mb-0.5">Private Connection</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    We facilitate introductions via private messaging or email
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-medium mb-0.5">Close the Deal</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Negotiate terms and finalize your joint venture partnership
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* CTA Section */}
      <div className="text-center">
        <Button 
          size="lg" 
          onClick={onNavigateToForm}
          className="text-xs sm:text-sm font-medium h-9 sm:h-10"
        >
          Submit Joint Venture Interest
        </Button>
        <p className="text-xs sm:text-sm text-muted-foreground mt-3 sm:mt-4">
          Ready to start? Submit your interest and we'll match you with the right partner.
        </p>
      </div>
    </div>
  );
}

// ============================================
// FORM COMPONENT (Collapsible)
// ============================================
function JVFormPage({ onBack }) {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [isExpanded, setIsExpanded] = useState(false);
  const [userType, setUserType] = useState('LAND_OWNER');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: user?.email || '',
    phone: '',
    county: '',
    subCounty: '',
    timeline: '',
    landSize: '',
    titleDeedStatus: '',
    developmentType: '',
    landValue: '',
    capitalNeeded: '',
    landDetails: '',
    investmentAmount: '',
    investmentMin: '',
    investmentMax: '',
    projectTypeInterest: '',
    experienceLevel: '',
    landSizeMin: '',
    landSizeMax: '',
    investorNotes: ''
  });

  const selectedCounty = counties.find(c => c.name === formData.county);
  const subCounties = selectedCounty?.subCounties || [];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'county' ? { subCounty: '' } : {})
    }));
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim().length < 2) {
      toast({ variant: 'destructive', title: 'Invalid Name', description: 'Name must be at least 2 characters.' });
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      toast({ variant: 'destructive', title: 'Invalid Email', description: 'Please enter a valid email address.' });
      return false;
    }

    const phoneRegex = /^254[17]\d{8}$/;
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!cleanPhone || !phoneRegex.test(cleanPhone)) {
      toast({ variant: 'destructive', title: 'Invalid Phone', description: 'Format: 254712345678 (Kenyan number)' });
      return false;
    }

    if (!formData.county) {
      toast({ variant: 'destructive', title: 'Missing County', description: 'Please select a county.' });
      return false;
    }

    if (!formData.subCounty) {
      toast({ variant: 'destructive', title: 'Missing Sub-County', description: 'Please select a sub-county.' });
      return false;
    }

    if (!formData.timeline) {
      toast({ variant: 'destructive', title: 'Missing Timeline', description: 'Please select a timeline.' });
      return false;
    }

    if (userType === 'LAND_OWNER') {
      if (!formData.landSize || parseFloat(formData.landSize) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Land Size', description: 'Please enter a valid land size.' });
        return false;
      }
      if (!formData.titleDeedStatus) {
        toast({ variant: 'destructive', title: 'Missing Deed Status', description: 'Please select title deed status.' });
        return false;
      }
      if (!formData.developmentType) {
        toast({ variant: 'destructive', title: 'Missing Development Type', description: 'Please select development type.' });
        return false;
      }
      if (!formData.landValue || parseFloat(formData.landValue) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Land Value', description: 'Please enter a valid land value.' });
        return false;
      }
      if (!formData.capitalNeeded || parseFloat(formData.capitalNeeded) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Capital', description: 'Please enter capital needed.' });
        return false;
      }
      const details = formData.landDetails.trim();
      if (details.length < 20) {
        toast({ variant: 'destructive', title: 'Description Too Short', description: 'Description must be at least 20 characters.' });
        return false;
      }
    }

    if (userType === 'INVESTOR') {
      if (!formData.investmentAmount || parseFloat(formData.investmentAmount) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Amount', description: 'Please enter investment amount.' });
        return false;
      }
      if (!formData.experienceLevel) {
        toast({ variant: 'destructive', title: 'Missing Experience', description: 'Please select experience level.' });
        return false;
      }
      if (!formData.investmentMin || parseFloat(formData.investmentMin) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Min Investment', description: 'Please enter minimum investment.' });
        return false;
      }
      if (!formData.investmentMax || parseFloat(formData.investmentMax) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Max Investment', description: 'Please enter maximum investment.' });
        return false; 
      }  
      if (parseFloat(formData.investmentMax) < parseFloat(formData.investmentMin)) {
        toast({ variant: 'destructive', title: 'Invalid Investment Range', description: 'Maximum investment must be greater than minimum investment.' });
        return false;
      }
      if (!formData.projectTypeInterest) {
        toast({ variant: 'destructive', title: 'Missing Project Type', description: 'Please select project type.' });
        return false;
      }
      if (!formData.landSizeMin || parseFloat(formData.landSizeMin) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Min Size', description: 'Please enter minimum land size.' });
        return false;
      }
      if (!formData.landSizeMax || parseFloat(formData.landSizeMax) <= 0) {
        toast({ variant: 'destructive', title: 'Invalid Max Size', description: 'Please enter maximum land size.' });
        return false;
      }
      if (parseFloat(formData.landSizeMax) < parseFloat(formData.landSizeMin)) {
        toast({ variant: 'destructive', title: 'Invalid Land Size Range', description: 'Maximum land size must be greater than minimum land size.' });
        return false;
      }  
      
      const notes = formData.investorNotes.trim();
      if (notes.length < 20) {
        toast({ variant: 'destructive', title: 'Notes Too Short', description: 'Notes must be at least 20 characters.' });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate('/login');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        role: userType,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        county: formData.county,
        subCounty: formData.subCounty,
        timeline: formData.timeline,
        details: userType === 'LAND_OWNER' ? formData.landDetails : formData.investorNotes,
      };

      if (userType === 'LAND_OWNER') {
        submissionData.landSize = parseFloat(formData.landSize);
        submissionData.titleDeedStatus = formData.titleDeedStatus;
        submissionData.developmentType = formData.developmentType;
        submissionData.landValue = parseFloat(formData.landValue);
        submissionData.capitalNeeded = parseFloat(formData.capitalNeeded);
        submissionData.landDetails = formData.landDetails;
      } else {
        submissionData.investmentAmount = parseFloat(formData.investmentAmount);
        submissionData.investmentMin = parseFloat(formData.investmentMin);
        submissionData.investmentMax = parseFloat(formData.investmentMax);
        submissionData.projectTypeInterest = formData.projectTypeInterest;
        submissionData.experienceLevel = formData.experienceLevel;
        submissionData.landSizeMin = parseFloat(formData.landSizeMin);
        submissionData.landSizeMax = parseFloat(formData.landSizeMax);
        submissionData.investorNotes = formData.investorNotes;
      }

      const dashboardResponse = await dashboardAPI.getOverview();
      const jvSubscriptionEnd = dashboardResponse.dashboard?.subscription?.jvSubscriptionEnd;
      
      const hasActiveSubscription = 
        jvSubscriptionEnd && 
        new Date(jvSubscriptionEnd) > new Date();

      if (hasActiveSubscription) {
        const listingResponse = await jointVenturesAPI.createJVListingDirect(submissionData);
        
        if (listingResponse.success) {
          toast({
            title: 'Success!',
            description: 'Your JV listing has been created.'
          });
          navigate('/jv/my-submission');
        }
      } else {
        navigate('/jv/checkout', {
          state: { 
            formData: submissionData,
            requiresPayment: true
          }
        });
      }
    } catch (err) {
      console.error('[SubmitJV] Error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err.response?.data?.message || 'Failed to process your submission'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="container py-8 flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-6 sm:py-8">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
          Back
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs sm:text-sm">
            Please <a href="/login" className="underline">login</a> to submit your joint venture interest.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-6 sm:py-8 max-w-4xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
        Back
      </Button>

      {/* Collapsible Form Card */}
      <Card className="bg-muted/40">
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors relative"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm sm:text-base md:text-lg">
                Submit Joint Venture Interest
              </CardTitle>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Fill in your details. We'll contact you privately if we find a match.
              </p>
            </div>
            {isExpanded ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
            )}
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* USER TYPE SELECTION */}
              <div className="space-y-2 sm:space-y-3">
                <Label className="text-xs sm:text-sm">I am a:</Label>
                <RadioGroup value={userType} onValueChange={setUserType}>
                  <div className="flex items-center space-x-2 border rounded-lg p-3 sm:p-4 cursor-pointer hover:bg-accent">
                    <RadioGroupItem value="LAND_OWNER" id="landowner" />
                    <Label htmlFor="landowner" className="flex items-center gap-2 cursor-pointer flex-1 text-xs sm:text-sm">
                      <UserCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Land Owner</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">I have land and need capital/partner</div>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border rounded-lg p-3 sm:p-4 cursor-pointer hover:bg-accent">
                    <RadioGroupItem value="INVESTOR" id="investor" />
                    <Label htmlFor="investor" className="flex items-center gap-2 cursor-pointer flex-1 text-xs sm:text-sm">
                      <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Investor</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">I have capital and looking for land projects</div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* COMMON FIELDS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                <div className="space-y-1 sm:space-y-1.5">
                  <Label htmlFor="name" className="text-xs sm:text-sm">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="text-xs sm:text-sm h-9 sm:h-10"
                    required
                  />
                </div>

                <div className="space-y-1 sm:space-y-1.5">
                  <Label htmlFor="email" className="text-xs sm:text-sm">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="text-xs sm:text-sm h-9 sm:h-10"
                    required
                  />
                </div>

                <div className="space-y-1 sm:space-y-1.5">
                  <Label htmlFor="phone" className="text-xs sm:text-sm">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+254..."
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="text-xs sm:text-sm h-9 sm:h-10"
                    required
                  />
                </div>

                <div className="space-y-1 sm:space-y-1.5">
                  <Label htmlFor="county" className="text-xs sm:text-sm">County *</Label>
                  <Select value={formData.county} onValueChange={(val) => handleChange('county', val)} required>
                    <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                      <SelectValue placeholder="Select county" />
                    </SelectTrigger>
                    <SelectContent>
                      {counties.map(county => (
                        <SelectItem key={county.name} value={county.name} className="text-xs sm:text-sm">
                          {county.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.county && (
                  <div className="space-y-1 sm:space-y-1.5">
                    <Label htmlFor="subCounty" className="text-xs sm:text-sm">Sub-County *</Label>
                    <Select value={formData.subCounty} onValueChange={(val) => handleChange('subCounty', val)} required>
                      <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                        <SelectValue placeholder="Select sub-county" />
                      </SelectTrigger>
                      <SelectContent>
                        {subCounties.map(subCounty => (
                          <SelectItem key={subCounty} value={subCounty} className="text-xs sm:text-sm">
                            {subCounty}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-1 sm:space-y-1.5">
                  <Label htmlFor="timeline" className="text-xs sm:text-sm">Project Timeline *</Label>
                  <Select value={formData.timeline} onValueChange={(val) => handleChange('timeline', val)} required>
                    <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map(timeline => (
                        <SelectItem key={timeline} value={timeline} className="text-xs sm:text-sm">
                          {timeline}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* LANDOWNER FIELDS */}
              {userType === 'LAND_OWNER' && (
                <div className="space-y-3 sm:space-y-4 border-t pt-3 sm:pt-4">
                  <h3 className="text-sm sm:text-base font-medium">Land Details</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="landSize" className="text-xs sm:text-sm">Land Size (Acres) *</Label>
                      <Input
                        id="landSize"
                        type="number"
                        step="0.01"
                        value={formData.landSize}
                        onChange={(e) => handleChange('landSize', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="titleDeedStatus" className="text-xs sm:text-sm">Title Deed Status *</Label>
                      <Select value={formData.titleDeedStatus} onValueChange={(val) => handleChange('titleDeedStatus', val)} required>
                        <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {TITLE_DEED_STATUS.map(status => (
                            <SelectItem key={status.value} value={status.value} className="text-xs sm:text-sm">
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="developmentType" className="text-xs sm:text-sm">Development Type *</Label>
                      <Select value={formData.developmentType} onValueChange={(val) => handleChange('developmentType', val)} required>
                        <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEVELOPMENT_TYPES.map(type => (
                            <SelectItem key={type} value={type} className="text-xs sm:text-sm">
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="landValue" className="text-xs sm:text-sm">Land Value (KES) *</Label>
                      <Input
                        id="landValue"
                        type="number"
                        placeholder="5000000"
                        value={formData.landValue}
                        onChange={(e) => handleChange('landValue', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="capitalNeeded" className="text-xs sm:text-sm">Capital Needed (KES) *</Label>
                      <Input
                        id="capitalNeeded"
                        type="number"
                        placeholder="10000000"
                        value={formData.capitalNeeded}
                        onChange={(e) => handleChange('capitalNeeded', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1 sm:space-y-1.5">
                    <Label htmlFor="landDetails" className="text-xs sm:text-sm">Additional Details *</Label>
                    <Textarea
                      id="landDetails"
                      placeholder="Any additional information about your land or project..."
                      value={formData.landDetails}
                      onChange={(e) => handleChange('landDetails', e.target.value)}
                      className="text-xs sm:text-sm min-h-[80px]"
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {/* INVESTOR FIELDS */}
              {userType === 'INVESTOR' && (
                <div className="space-y-3 sm:space-y-4 border-t pt-3 sm:pt-4">
                  <h3 className="text-sm sm:text-base font-medium">Investment Details</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="investmentAmount" className="text-xs sm:text-sm">Investment Amount (KES) *</Label>
                      <Input
                        id="investmentAmount"
                        type="number"
                        placeholder="15000000"
                        value={formData.investmentAmount}
                        onChange={(e) => handleChange('investmentAmount', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="experienceLevel" className="text-xs sm:text-sm">Experience Level *</Label>
                      <Select value={formData.experienceLevel} onValueChange={(val) => handleChange('experienceLevel', val)} required>
                        <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPERIENCE_LEVELS.map(level => (
                            <SelectItem key={level.value} value={level.value} className="text-xs sm:text-sm">
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="investmentMin" className="text-xs sm:text-sm">Min Investment (KES) *</Label>
                      <Input
                        id="investmentMin"
                        type="number"
                        placeholder="5000000"
                        value={formData.investmentMin}
                        onChange={(e) => handleChange('investmentMin', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="investmentMax" className="text-xs sm:text-sm">Max Investment (KES) *</Label>
                      <Input
                        id="investmentMax"
                        type="number"
                        placeholder="20000000"
                        value={formData.investmentMax}
                        onChange={(e) => handleChange('investmentMax', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="projectTypeInterest" className="text-xs sm:text-sm">Project Type Interest *</Label>
                      <Select value={formData.projectTypeInterest} onValueChange={(val) => handleChange('projectTypeInterest', val)} required>
                        <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEVELOPMENT_TYPES.map(type => (
                            <SelectItem key={type} value={type} className="text-xs sm:text-sm">
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="landSizeMin" className="text-xs sm:text-sm">Min Land Size (Acres) *</Label>
                      <Input
                        id="landSizeMin"
                        type="number"
                        step="0.01"
                        placeholder="2"
                        value={formData.landSizeMin}
                        onChange={(e) => handleChange('landSizeMin', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>

                    <div className="space-y-1 sm:space-y-1.5">
                      <Label htmlFor="landSizeMax" className="text-xs sm:text-sm">Max Land Size (Acres) *</Label>
                      <Input
                        id="landSizeMax"
                        type="number"
                        step="0.01"
                        placeholder="10"
                        value={formData.landSizeMax}
                        onChange={(e) => handleChange('landSizeMax', e.target.value)}
                        className="text-xs sm:text-sm h-9 sm:h-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1 sm:space-y-1.5">
                    <Label htmlFor="investorNotes" className="text-xs sm:text-sm">Additional Notes *</Label>
                    <Textarea
                      id="investorNotes"
                      placeholder="Any specific requirements or preferences..."
                      value={formData.investorNotes}
                      onChange={(e) => handleChange('investorNotes', e.target.value)}
                      className="text-xs sm:text-sm min-h-[80px]"
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {/* SUBMIT BUTTON */}
              <div className="flex gap-2 sm:gap-3">
                <Button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="flex-1 text-xs sm:text-sm h-9 sm:h-10 font-medium"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Interest'
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onBack}
                  className="text-xs sm:text-sm h-9 sm:h-10 font-medium"
                >
                  Cancel
                </Button>
              </div>

              {/* DISCLAIMER */}
              <div className="text-xs sm:text-sm text-muted-foreground border-t pt-3 sm:pt-4">
                <p>
                  ⚠️ By submitting, you agree that we may contact you if we find a suitable match. 
                  Your information remains private and is never publicly displayed.
                </p>
              </div>
            </form>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

// ============================================
// MAIN COMPONENT WITH NAVIGATION
// ============================================
export default function SubmitJVPage() {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('landing'); // 'landing' or 'form'

  const handleNavigateToForm = () => {
    setCurrentView('form');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleBackToLanding = () => {
    setCurrentView('landing');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <div className="border-b bg-card">
        <div className="container py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              variant={currentView === 'landing' ? 'default' : 'outline'}
              onClick={() => setCurrentView('landing')}
              className="text-xs sm:text-sm font-medium h-9 sm:h-10"
            >
              About Joint Ventures
            </Button>
            <Button
              variant={currentView === 'form' ? 'default' : 'outline'}
              onClick={() => setCurrentView('form')}
              className="text-xs sm:text-sm font-medium h-9 sm:h-10"
            >
              Submit Joint Venture Interest
            </Button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {currentView === 'landing' ? (
        <JVLandingPage 
          onNavigateToForm={handleNavigateToForm}
          onBack={handleBackToDashboard}
        />
      ) : (
        <JVFormPage 
          onBack={handleBackToLanding}
        />
      )}
    </div>
  );
}